import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import PaymentReceiptDocument from './PaymentReceiptDocument';
import { printHtml } from '../utils/printHtml';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface BillSummary {
  id: number;
  /** NULL for consumables — invoice_no is their number. */
  bill_no: number | null;
  invoice_no?: string | null;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  status: string;
  receipt_pdf_url: string | null;
  has_payment_receipt: boolean;
}

interface Patient {
  id: number;
  patient_name: string;
  patient_salutation?: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  address: string;
  patient_clients: { salutation?: string; client_name: string; contact_number_1: string; email: string }[];
}

interface Props {
  bill: BillSummary;
  patient: Patient;
  onClose: () => void;
}

const PaymentReceiptPreview: React.FC<Props> = ({ bill, patient, onClose }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<any[]>([]);
  const [collectedBy, setCollectedBy] = useState('');
  const [paymentMadeBy, setPaymentMadeBy] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/billing/bills/${bill.id}/payment-receipt`,
          { headers },
        );
        setEntries(res.data.entries || []);
        setCollectedBy(res.data.collected_by || '');
        setPaymentMadeBy(res.data.payment_made_by || '');
      } catch (err: any) {
        if (err.response?.status !== 404) toast.error('Failed to load receipt entries');
      } finally {
        setLoading(false);
      }
    };
    fetchEntries();
  }, [bill.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    if (!receiptRef.current) return;
    setDownloading(true);
    const fmtD = (d: string) => d.split('-').reverse().join('-');
    const fileName = `Payment_Receipt_${patient.patient_name}_${bill.bill_no}_${fmtD(bill.invoice_period_start)} to ${fmtD(bill.invoice_period_end)}`;
    printHtml(fileName, receiptRef.current.outerHTML, () => setDownloading(false));
  };

  return (
    <>
      <div
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.75)',
          zIndex: 6000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '30px', overflowY: 'auto',
        }}
        onClick={onClose}
      >
        <div
          style={{ width: '860px', maxWidth: '96vw', paddingBottom: '60px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Toolbar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '12px',
          }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { onClose(); navigate(`/billing/receipt/${bill.id}`); }}
                style={{
                  padding: '7px 16px', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem',
                  color: '#334155', fontWeight: 500,
                }}
              >
                Edit Payment Details
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading || loading}
                style={{
                  padding: '7px 16px', backgroundColor: '#2563eb', border: 'none',
                  borderRadius: '8px', cursor: downloading ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem', color: 'white', fontWeight: 500,
                  opacity: downloading ? 0.7 : 1,
                }}
              >
                {downloading ? 'Downloading…' : '⬇ Download PDF'}
              </button>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#e2e8f0', fontSize: '1.5rem', lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Document */}
          {loading ? (
            <div style={{ color: 'white', textAlign: 'center', padding: '3rem' }}>Loading…</div>
          ) : (
            <div style={{ margin: '0 auto', width: '794px' }}>
              <PaymentReceiptDocument
                ref={receiptRef}
                bill={bill}
                patient={patient}
                entries={entries}
                collectedBy={collectedBy || 'LHHS Team'}
                paymentMadeBy={paymentMadeBy || undefined}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentReceiptPreview;
